import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaTrashRestore, FaBoxOpen, FaBan } from 'react-icons/fa';
import { jwtDecode } from "jwt-decode";
import * as api from "../../api/index";

const BlockedProduct = () => {
  const navigate = useNavigate();
  // ✅ Fix 1: Ensure initial state is always an empty array
  const [blockedProducts, setBlockedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);

  // ✅ Fix 2: Wrap fetch in useCallback to prevent re-renders
  const fetchBlockedProducts = useCallback(async (myId) => {
    try {
      setLoading(true);
      const res = await api.getBlockedProducts(myId);
      
      // ✅ Fix 3: Added more robust data checking
      if (res?.data?.success && Array.isArray(res.data.blockedItems)) {
        setBlockedProducts(res.data.blockedItems);
      } else {
        // Fallback to empty array if data is missing or wrong format
        setBlockedProducts([]);
      }
    } catch (err) {
      console.error("Error fetching blocked products:", err);
      setBlockedProducts([]); // Ensure state isn't undefined on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const myId = decoded.id || decoded._id;
        setUserId(myId);
        fetchBlockedProducts(myId);
      } catch (e) {
        console.error("Token decode error");
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [fetchBlockedProducts]);

  const handleUnblock = async (productId) => {
    if (!window.confirm("Restore this product to your feed?")) return;

    try {
      const res = await api.unblockProduct({ userId, productId });
      if (res?.data?.success) {
        setBlockedProducts((prev) => 
          // ✅ Fix 4: Safety check before filtering
          (prev || []).filter((item) => item._id !== productId)
        );
      }
    } catch (err) {
      alert("Failed to unblock product.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center justify-between bg-white px-6 py-4 border-b border-gray-100 shadow-sm">
        <button 
          onClick={() => navigate(-1)} 
          className="p-2 hover:bg-gray-100 rounded-full transition-all"
        >
          <FaArrowLeft size={20} className="text-gray-700" />
        </button>
        <h1 className="text-lg font-bold text-gray-800">Blocked Products</h1>
        <div className="w-10" />
      </div>

      <div className="max-w-4xl mx-auto p-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
            <p className="mt-4 text-gray-500 font-medium">Fetching blocked items...</p>
          </div>
        ) : (blockedProducts && blockedProducts.length > 0) ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {blockedProducts.map((product) => (
              <div 
                key={product._id} 
                className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex items-center space-x-4 hover:border-orange-200 transition-all"
              >
                <div className="relative h-20 w-20 flex-shrink-0">
                  <img 
                    src={product.images?.[0] || 'https://via.placeholder.com/150'} 
                    alt={product.name}
                    className="h-full w-full object-cover rounded-xl grayscale opacity-50"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <FaBan size={18} className="text-gray-400 opacity-80" />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-gray-800 truncate">{product.name}</h3>
                  <p className="text-orange-600 font-black text-sm mt-1">₦{product.price?.toLocaleString()}</p>
                </div>

                <button 
                  onClick={() => handleUnblock(product._id)}
                  className="flex flex-col items-center justify-center px-4 py-2 bg-orange-50 hover:bg-orange-100 rounded-xl transition-colors"
                >
                  <FaTrashRestore size={18} className="text-orange-600" />
                  <span className="text-[10px] font-bold text-orange-600 mt-1 uppercase">Restore</span>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="bg-white p-8 rounded-full shadow-inner mb-6">
              <FaBoxOpen size={60} className="text-gray-200" />
            </div>
            <h2 className="text-xl font-bold text-gray-700">Clean Feed!</h2>
            <p className="text-gray-400 mt-2 max-w-xs">
              You haven't blocked any products yet. Listings you hide will show up here.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlockedProduct;